import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Copy, PackageCheck } from "lucide-react";
import { API, EMAIL, PHONE, PHONE_LINK, SERVICE_OPTIONS, WA_LINK } from "@/constants/site";

const inputCls =
  "w-full px-4 py-3 rounded-lg bg-black/40 border border-white/10 text-white text-xs focus:outline-none focus:border-pink-500 placeholder:text-slate-500";
const labelCls = "block text-xs font-mono uppercase tracking-wider text-slate-400 mb-2";

const ContactSection = () => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: SERVICE_OPTIONS[0], message: "" });
  const [sending, setSending] = useState(false);
  const [tab, setTab] = useState("order");
  const [confirmation, setConfirmation] = useState(null);
  const [copied, setCopied] = useState(false);
  const [track, setTrack] = useState({ ref: "", phone: "" });
  const [trackResult, setTrackResult] = useState(null);
  const [trackError, setTrackError] = useState("");
  const [tracking, setTracking] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/enquiries`, { ...form, email: form.email || null });
      toast.success("Order request received — we reply 24/7, usually within hours.");
      setConfirmation({ ref: res.data.ref });
      setForm({ name: "", phone: "", email: "", service: SERVICE_OPTIONS[0], message: "" });
    } catch (err) {
      toast.error("Could not send your request. Please try WhatsApp or the helpline instead.");
    } finally {
      setSending(false);
    }
  };

  const copyRef = async () => {
    try {
      await navigator.clipboard.writeText(confirmation.ref);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error("Copy failed — please note the reference manually.");
    }
  };

  const submitTrack = async (e) => {
    e.preventDefault();
    if (tracking) return;
    setTracking(true);
    setTrackError("");
    setTrackResult(null);
    try {
      const res = await axios.get(`${API}/enquiries/track`, {
        params: { ref: track.ref, phone: track.phone },
      });
      setTrackResult(res.data);
    } catch (err) {
      setTrackError("No order found for that reference and phone number.");
    } finally {
      setTracking(false);
    }
  };

  return (
    <section id="contact" className="py-24 px-6 bg-[#110e1a]" data-testid="contact-section">
      <div className="max-w-6xl mx-auto grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <span className="text-xs font-mono uppercase tracking-widest text-pink-400">Immediate Helpline</span>
          <h2 className="font-poster text-3xl sm:text-4xl uppercase text-white mt-2 mb-4">Direct Contact Rail</h2>
          <div className="space-y-4 font-mono text-xs">
            <a
              href={PHONE_LINK}
              data-testid="contact-phone-card"
              className="block p-4 rounded-xl border border-white/10 bg-white/[0.02] hover:border-pink-500/40 transition-colors"
            >
              <span className="text-slate-400 block mb-1">DIRECT PHONE NUMBER (TAP TO CALL)</span>
              <span className="text-white font-bold text-sm">{PHONE}</span>
            </a>
            <a
              href={WA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-card"
              className="block p-4 rounded-xl border border-white/10 bg-white/[0.02] hover:border-emerald-500/40 transition-colors"
            >
              <span className="text-slate-400 block mb-1">WHATSAPP (24/7)</span>
              <span className="text-white font-bold text-sm">{PHONE}</span>
            </a>
            <a
              href={`mailto:${EMAIL}`}
              data-testid="contact-email-card"
              className="block p-4 rounded-xl border border-white/10 bg-white/[0.02] hover:border-pink-500/40 transition-colors"
            >
              <span className="text-slate-400 block mb-1">CONTACT EMAIL</span>
              <span className="text-white font-bold text-sm">{EMAIL}</span>
            </a>
            <div className="p-4 rounded-xl border border-white/10 bg-white/[0.02]">
              <span className="text-slate-400 block mb-1">DOMAIN ADDRESS</span>
              <span className="text-pink-300 font-bold text-sm">chatblackhole.com</span>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          {confirmation ? (
            <div
              data-testid="enquiry-confirmation"
              className="p-8 rounded-2xl border border-emerald-500/30 bg-[#171324] text-center space-y-5"
            >
              <PackageCheck className="mx-auto text-emerald-400" size={40} />
              <h3 className="font-poster text-3xl uppercase text-white">Order Received</h3>
              <p className="text-sm text-slate-300 max-w-md mx-auto leading-relaxed">
                Save your reference ID. Quote it on WhatsApp or the helpline anytime for a status update.
              </p>
              <div
                data-testid="enquiry-ref-code"
                className="inline-block font-mono text-3xl sm:text-4xl font-bold tracking-widest text-emerald-300 bg-black/40 border border-emerald-500/30 rounded-xl py-4 px-6"
              >
                {confirmation.ref}
              </div>
              <div className="flex flex-wrap justify-center gap-3 pt-2">
                <button
                  type="button"
                  onClick={copyRef}
                  data-testid="copy-ref-btn"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-white text-xs font-mono uppercase tracking-wider transition-colors"
                >
                  {copied ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
                  {copied ? "Copied" : "Copy Reference"}
                </button>
                <a
                  href={`${WA_LINK}?text=${encodeURIComponent(`Hi, my order reference is ${confirmation.ref}`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="whatsapp-ref-link"
                  className="px-5 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-white text-xs font-bold font-mono uppercase tracking-wider transition-colors"
                >
                  Send Ref on WhatsApp
                </a>
                <button
                  type="button"
                  onClick={() => setConfirmation(null)}
                  data-testid="new-enquiry-btn"
                  className="px-5 py-3 rounded-xl text-slate-400 hover:text-white text-xs font-mono uppercase tracking-wider transition-colors"
                >
                  New Request
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex gap-2 mb-4" data-testid="contact-tabs">
                <button
                  type="button"
                  onClick={() => setTab("order")}
                  data-testid="new-order-tab"
                  className={`px-5 py-2.5 rounded-lg text-xs font-mono uppercase tracking-wider transition-colors ${tab === "order" ? "bg-pink-500 text-white font-bold" : "bg-white/5 border border-white/10 text-slate-400 hover:text-white"}`}
                >
                  New Order
                </button>
                <button
                  type="button"
                  onClick={() => setTab("track")}
                  data-testid="track-order-tab"
                  className={`px-5 py-2.5 rounded-lg text-xs font-mono uppercase tracking-wider transition-colors ${tab === "track" ? "bg-pink-500 text-white font-bold" : "bg-white/5 border border-white/10 text-slate-400 hover:text-white"}`}
                >
                  Track Order
                </button>
              </div>
              {tab === "order" ? (
          <form
            onSubmit={submit}
            data-testid="enquiry-form"
            className="p-8 rounded-2xl border border-white/10 bg-[#171324] space-y-4"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls} htmlFor="enquiry-name">Name</label>
                <input
                  id="enquiry-name"
                  data-testid="enquiry-name-input"
                  type="text"
                  required
                  value={form.name}
                  onChange={set("name")}
                  className={inputCls}
                  placeholder="Your Name"
                />
              </div>
              <div>
                <label className={labelCls} htmlFor="enquiry-phone">WhatsApp / Phone</label>
                <input
                  id="enquiry-phone"
                  data-testid="enquiry-phone-input"
                  type="text"
                  required
                  value={form.phone}
                  onChange={set("phone")}
                  className={inputCls}
                  placeholder="+92..."
                />
              </div>
            </div>
            <div>
              <label className={labelCls} htmlFor="enquiry-email">Email (optional)</label>
              <input
                id="enquiry-email"
                data-testid="enquiry-email-input"
                type="email"
                value={form.email}
                onChange={set("email")}
                className={inputCls}
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className={labelCls} htmlFor="enquiry-service">Service Category</label>
              <select
                id="enquiry-service"
                data-testid="enquiry-service-select"
                value={form.service}
                onChange={set("service")}
                className="w-full px-4 py-3 rounded-lg bg-[#201a33] border border-white/10 text-white text-xs focus:outline-none focus:border-pink-500"
              >
                {SERVICE_OPTIONS.map((o) => (
                  <option key={o}>{o}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls} htmlFor="enquiry-message">Message &amp; Timeline</label>
              <textarea
                id="enquiry-message"
                data-testid="enquiry-message-input"
                rows={3}
                required
                value={form.message}
                onChange={set("message")}
                className={inputCls}
                placeholder="Describe your assignment or urgent project..."
              />
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="enquiry-submit-btn"
              className="w-full py-4 rounded-xl bg-pink-500 hover:bg-pink-600 disabled:opacity-60 text-white font-bold text-xs uppercase font-mono tracking-wider transition-colors shadow-lg shadow-pink-500/25"
            >
              {sending ? "Sending..." : "Submit Order Request"}
            </button>
          </form>
              ) : (
                <form
                  onSubmit={submitTrack}
                  data-testid="track-form"
                  className="p-8 rounded-2xl border border-white/10 bg-[#171324] space-y-4"
                >
                  <p className="text-xs text-slate-400 font-mono">
                    Enter your reference ID and the phone number you ordered with.
                  </p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className={labelCls} htmlFor="track-ref">Reference ID</label>
                      <input
                        id="track-ref"
                        data-testid="track-ref-input"
                        type="text"
                        required
                        value={track.ref}
                        onChange={(e) => setTrack((t) => ({ ...t, ref: e.target.value }))}
                        className={inputCls}
                        placeholder="CBH-XXXXXX"
                      />
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="track-phone">Phone / WhatsApp</label>
                      <input
                        id="track-phone"
                        data-testid="track-phone-input"
                        type="text"
                        required
                        value={track.phone}
                        onChange={(e) => setTrack((t) => ({ ...t, phone: e.target.value }))}
                        className={inputCls}
                        placeholder="+92..."
                      />
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={tracking}
                    data-testid="track-submit-btn"
                    className="w-full py-4 rounded-xl bg-pink-500 hover:bg-pink-600 disabled:opacity-60 text-white font-bold text-xs uppercase font-mono tracking-wider transition-colors shadow-lg shadow-pink-500/25"
                  >
                    {tracking ? "Checking..." : "Track My Order"}
                  </button>
                  {trackError && (
                    <p data-testid="track-error" className="text-rose-400 text-xs font-mono text-center">
                      {trackError}
                    </p>
                  )}
                  {trackResult && (
                    <div
                      data-testid="track-result"
                      className="p-5 rounded-xl border border-emerald-500/30 bg-black/30 font-mono text-xs space-y-2"
                    >
                      <div className="flex items-center justify-between gap-4">
                        <span className="text-slate-400">STATUS</span>
                        <span
                          data-testid="track-status"
                          className="px-3 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 font-bold uppercase tracking-wider"
                        >
                          {trackResult.status}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-4">
                        <span className="text-slate-400">SERVICE</span>
                        <span className="text-white text-right">{trackResult.service}</span>
                      </div>
                      <div className="flex items-center justify-between gap-4">
                        <span className="text-slate-400">SUBMITTED</span>
                        <span className="text-white">{new Date(trackResult.created_at).toLocaleString()}</span>
                      </div>
                      <p className="text-slate-400 pt-1">
                        For the latest update, message us on WhatsApp quoting{" "}
                        <span className="text-emerald-300">{trackResult.ref}</span>.
                      </p>
                    </div>
                  )}
                </form>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
